import { useEffect, useState } from "react";

import { type BMode, LUNCH_HOUR, computeBMode } from "../lib/adhd";
import { type AdhdVariant, type DailySummaryView, type TargetsView, api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { todayIso } from "../lib/day";
import { DailyTrackerScreen } from "./daily-tracker-screen";
import { HomeAdhdA } from "./home-adhd-a";
import { type BPalette, HomeAdhdB } from "./home-adhd-b";
import { ProfileSwitcher } from "./profile-switcher";
import { AdhdChooser } from "./ui/adhd-chooser";
import { AdhdNav } from "./ui/adhd-nav";
import { AdhdPill } from "./ui/adhd-pill";
import { BottomNav } from "./ui/bottom-nav";
import { SytoLogo } from "./ui/syto-logo";

// HomeMaybeADHD — the Home route entry point (ADR-0015, design handoff
// §"Architektura komponentów").
//
// ADHD off: render the full DailyTrackerScreen with the discreet pill inline
//           in the header, plus the standard BottomNav.
// ADHD on:  render the shared shell (logo + pill + chooser + nav) ONCE, and
//           switch only the body between variants A/B/C. The chooser stays
//           MOUNTED across variant changes — fix for the "klik nic nie robi
//           przez 20 razy" intermittent dead-clicks where the chooser's DOM
//           element used to be torn down and remounted on every switch.
//
// It owns the enabled/variant state, persists changes via api.setAdhd
// (fire-and-forget, like setLanguage) and patches the auth context
// optimistically so the UI flips immediately.

type Props = {
  householdId: string;
  targets: TargetsView;
  // Re-mount key bumped by App.tsx after a meal is logged elsewhere.
  reloadKey: number;
  // Nav callbacks — App.tsx owns routing.
  onFridge: () => void;
  onBulb: () => void;
  onMe: () => void;
  onAdd: () => void;
  // Forces a Home data reload (e.g. after variant C logs a meal).
  onMealLogged: () => void;
  // ADR-0018: opens the Children screen from the profile switcher dropdown.
  onOpenChildren: () => void;
};

// Shell palette: drives <main> bg, logo / pill colors, and chooser tinting.
// Variant B (Sygnał) overrides per traffic-light bMode; other variants use
// the default token-driven palette so light/dark theme still adapts.
type ShellPalette = {
  bg: string;
  ink: string;
  hint: string;
  // Pill overrides — undefined falls back to the global tokens.
  pillInk?: string;
  pillBg?: string;
  pillMuted?: string;
  // Chooser overrides — same.
  chooserBg?: string;
  chooserInk?: string;
  chooserMuted?: string;
  // Logo colour override.
  logoColor?: string;
};

const DEFAULT_PALETTE: ShellPalette = {
  bg: "var(--bg)",
  ink: "var(--ink)",
  hint: "var(--ink-2)",
};

// Variant B's three traffic-light states. `ok` rides the global tokens so it
// adapts to light/dark theme; `soft`/`stop` are fixed semaphore colours per
// the handoff palette table.
const B_PALETTES: Record<BMode, ShellPalette & BPalette> = {
  ok: {
    bg: "var(--bg)",
    ink: "var(--ink)",
    hint: "var(--ink-2)",
    dot: "var(--accent)",
    chooserBg: "rgba(0,0,0,0.06)",
    chooserInk: "var(--ink)",
    chooserMuted: "var(--ink-2)",
  },
  soft: {
    bg: "#F4E4D6",
    ink: "#3F2A18",
    hint: "#6B4A2E",
    dot: "#C7642A",
    pillInk: "#3F2A18",
    pillBg: "#F4E4D6",
    pillMuted: "#6B4A2E",
    chooserBg: "rgba(63,42,24,0.08)",
    chooserInk: "#3F2A18",
    chooserMuted: "#6B4A2E",
    logoColor: "#3F2A18",
  },
  stop: {
    bg: "#16171B",
    ink: "#F0EFEC",
    hint: "#A8A6A0",
    dot: "#FF7A47",
    pillInk: "#F0EFEC",
    pillBg: "#16171B",
    pillMuted: "#A8A6A0",
    chooserBg: "rgba(255,255,255,0.08)",
    chooserInk: "#F0EFEC",
    chooserMuted: "#A8A6A0",
    logoColor: "#F0EFEC",
  },
};

export function HomeMaybeADHD({
  householdId,
  targets,
  reloadKey,
  onFridge,
  onBulb,
  onMe,
  onAdd,
  onMealLogged,
  onOpenChildren,
}: Props) {
  const { state, updateUser } = useAuth();
  const user = state.status === "signed-in" ? state.user : null;

  // The feature is gated on the flag: with it off, behave exactly like the
  // legacy Home (no pill, no ADHD path).
  const available = user?.adhdModeAvailable ?? false;
  const enabled = available && (user?.adhdEnabled ?? false);
  // Krok (variant C) chwilowo wyłączone z UI: users whose persisted
  // preference is "C" fall back to "A" at render. Backend accepts C in case
  // we re-enable it later; no UI surface lets them pick C in the meantime.
  const rawVariant = user?.adhdVariant ?? "A";
  const variant: AdhdVariant = rawVariant === "C" ? "A" : rawVariant;

  // Variant B traffic-light state. Computed from data unless the user has
  // tapped a state bar today — that manual override is session-only and
  // resets at midnight (handoff §"Reguły wyliczania bMode").
  const [bOverride, setBOverride] = useState<{ day: string; mode: BMode } | null>(null);

  // ADHD-mode data: remaining kcal drives variants A/B/C. Loaded here so the
  // variant components stay presentational.
  const [summary, setSummary] = useState<DailySummaryView | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .getDailySummary(householdId)
      .then((res) => {
        if (!cancelled) setSummary(res);
      })
      .catch(() => {
        // Non-fatal: variants fall back to a fresh-user view.
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, enabled, reloadKey]);

  function persist(nextEnabled: boolean, nextVariant: AdhdVariant) {
    // Optimistic local update, fire-and-forget persistence (like setLanguage).
    updateUser({ adhdEnabled: nextEnabled, adhdVariant: nextVariant });
    void api.setAdhd(nextEnabled, nextVariant).catch(() => undefined);
  }

  // === ADHD off: full Home + discreet pill rendered inline in the top bar ===
  if (!enabled) {
    const pill = available ? (
      <AdhdPill on={false} onToggle={() => persist(true, variant)} />
    ) : undefined;
    return (
      <>
        <DailyTrackerScreen
          key={reloadKey}
          householdId={householdId}
          targets={targets}
          headerLeft={<ProfileSwitcher onOpenChildren={onOpenChildren} />}
          {...(pill ? { headerRight: pill } : {})}
        />
        <BottomNav
          active="home"
          onHome={() => undefined}
          onFridge={onFridge}
          onBulb={onBulb}
          onMe={onMe}
          onAdd={onAdd}
        />
      </>
    );
  }

  // === ADHD on: shared shell + variant-specific body ===
  const kcalTarget = targets.kcalTarget ?? 0;
  const kcalConsumed = summary?.totals.kcal ?? 0;
  // Remaining can go negative (over the limit); variants handle that.
  const kcalRemaining = kcalTarget > 0 ? kcalTarget - kcalConsumed : 0;
  const hasData = (summary?.meals.length ?? 0) > 0;
  const hourLocal = new Date().getHours();
  const afterLunch = hourLocal >= LUNCH_HOUR;
  const today = todayIso();

  const computedBMode = computeBMode(kcalRemaining, hourLocal);
  const bMode = bOverride && bOverride.day === today ? bOverride.mode : computedBMode;

  const palette: ShellPalette = variant === "B" ? B_PALETTES[bMode] : DEFAULT_PALETTE;

  return (
    <main
      className="min-h-dvh w-full safe-top safe-bottom"
      style={{
        background: palette.bg,
        color: palette.ink,
        transition: "background-color 300ms ease-out, color 300ms ease-out",
      }}
    >
      <div
        className="flex flex-col mx-auto"
        style={{
          flex: 1,
          position: "relative",
          maxWidth: 480,
          padding: "14px 22px 110px",
          // overflowX only: prevent horizontal jiggle from the animated
          // palette transition but never block vertical scroll (gotchas
          // handoff §2.4 explicitly warns against `overflow: hidden` here
          // — it would strand content on shorter phones).
          overflowX: "hidden",
          minHeight: "100dvh",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <SytoLogo size={18} {...(palette.logoColor ? { color: palette.logoColor } : {})} />
            <ProfileSwitcher onOpenChildren={onOpenChildren} />
          </div>
          <AdhdPill
            on
            onToggle={() => persist(false, variant)}
            {...(palette.pillInk ? { inkColor: palette.pillInk } : {})}
            {...(palette.pillBg ? { bgColor: palette.pillBg } : {})}
            {...(palette.pillMuted ? { mutedColor: palette.pillMuted } : {})}
          />
        </div>
        <div style={{ marginTop: 14 }}>
          <AdhdChooser
            value={variant}
            onChange={(v) => persist(true, v)}
            {...(palette.chooserBg ? { bgColor: palette.chooserBg } : {})}
            {...(palette.chooserInk ? { inkColor: palette.chooserInk } : {})}
            {...(palette.chooserMuted ? { mutedColor: palette.chooserMuted } : {})}
          />
        </div>

        {variant === "A" && (
          <HomeAdhdA
            kcalRemaining={Math.max(0, kcalRemaining)}
            hasData={hasData}
            afterLunch={afterLunch}
          />
        )}
        {variant === "B" && (
          <HomeAdhdB
            bMode={bMode}
            onModeChange={(m) => setBOverride({ day: today, mode: m })}
            kcalRemaining={Math.max(0, kcalRemaining)}
            hasData={hasData}
            palette={B_PALETTES[bMode]}
          />
        )}
        {/* Variant C (Krok) chwilowo wyłączone — patrz adhd-chooser.tsx.
            Komponent HomeAdhdC zostaje w drzewie kodu na potem. */}

        <AdhdNav onExit={() => persist(false, variant)} onAdd={onAdd} />
      </div>
    </main>
  );
}
